import type { RouteObject } from 'react-router-dom'

/**
 * Meeting module route registration.
 * All pages are lazy-loaded to reduce initial bundle size.
 *
 * Personal meeting routes: /my-space/meetings/*
 * Team meeting routes: various (see below)
 * Settings routes: /settings/workspace/*
 */
export const meetingRoutes: RouteObject[] = [
  // ── Personal Meetings ──
  {
    path: 'meetings',
    lazy: () => import('./pages/MeetingListPage').then((m) => ({ Component: m.MeetingListPage })),
  },
  {
    path: 'meetings/create',
    lazy: () => import('./pages/CreateMeetingPage').then((m) => ({ Component: m.CreateMeetingPage })),
  },
  {
    path: 'meetings/:id',
    lazy: () => import('./pages/MeetingDetailPage').then((m) => ({ Component: m.MeetingDetailPage })),
  },
  {
    path: 'meetings/:id/notes',
    lazy: () => import('./pages/PersonalNotesPage').then((m) => ({ Component: m.PersonalNotesPage })),
  },
  {
    path: 'meetings/:id/actions',
    lazy: () => import('./pages/ActionItemsPage').then((m) => ({ Component: m.ActionItemsPage })),
  },
  {
    path: 'meetings/:id/edit',
    lazy: () => import('./pages/CreateMeetingPage').then((m) => {
      const React = require('react')
      return { Component: () => React.createElement(m.CreateMeetingPage, { mode: 'edit' }) }
    }),
  },
  // ── Documents ──
  {
    path: 'my-space/meetings/:id/docs',
    lazy: () =>
      import('./pages/DocumentsPage').then((m) => ({ Component: m.DocumentsPage })),
  },

  // ── Shared Notes ──
  {
    path: 'my-space/meetings/:id/notes/shared',
    lazy: () =>
      import('./pages/SharedNotesPage').then((m) => ({ Component: m.SharedNotesPage })),
  },

  // ── Workspace Meeting Settings ──
  {
    path: 'settings/workspace/meetings',
    lazy: () =>
      import('./pages/WorkspaceMeetingSettingsPage').then((m) => ({
        Component: () => {
          const { useWorkspaceContext } = require('@/app/providers/WorkspaceContext')
          const { currentWorkspaceId } = useWorkspaceContext()
          const { WorkspaceMeetingSettingsPage: Page } = m
          const React = require('react')
          return React.createElement(Page, { workspaceId: currentWorkspaceId })
        },
      })),
  },

  // ── Meeting Analytics ──
  {
    path: 'settings/workspace/meeting-analytics',
    lazy: () =>
      import('../analytics/pages/MeetingAnalyticsPage').then((m) => ({
        Component: () => {
          const { useWorkspaceContext } = require('@/app/providers/WorkspaceContext')
          const { currentWorkspaceId } = useWorkspaceContext()
          const { MeetingAnalyticsPage: Page } = m
          const React = require('react')
          return React.createElement(Page, { workspaceId: currentWorkspaceId })
        },
      })),
  },

  // ── Recurring Series Management ──
  {
    path: 'settings/workspace/meeting-series',
    lazy: () =>
      import('./pages/SeriesManagePage').then((m) => ({
        Component: () => {
          const { useWorkspaceContext } = require('@/app/providers/WorkspaceContext')
          const { currentWorkspaceId } = useWorkspaceContext()
          const { SeriesManagePage: Page } = m
          const React = require('react')
          return React.createElement(Page, { workspaceId: currentWorkspaceId })
        },
      })),
  },

  // ── Notification Preferences ──
  {
    path: 'settings/workspace/meeting-notifications',
    lazy: () =>
      import('./pages/NotificationPrefsPage').then((m) => ({
        Component: () => {
          const { useWorkspaceContext } = require('@/app/providers/WorkspaceContext')
          const { currentWorkspaceId } = useWorkspaceContext()
          const { NotificationPrefsPage: Page } = m
          const React = require('react')
          return React.createElement(Page, { workspaceId: currentWorkspaceId })
        },
      })),
  },

  // ── Team Action Board ──
  {
    path: 'team/action-board',
    lazy: () =>
      import('./pages/TeamActionBoardPage').then((m) => ({
        Component: () => {
          const { useWorkspaceContext } = require('@/app/providers/WorkspaceContext')
          const { currentWorkspaceId } = useWorkspaceContext()
          const { TeamActionBoardPage: Page } = m
          const React = require('react')
          return React.createElement(Page, { workspaceId: currentWorkspaceId })
        },
      })),
  },
]

// Re-export all public types and hooks for convenience
export type * from './types'
export * from './api'
